/**
 * The quest-readiness checklist, as a pure function of what the app already
 * knows about a member. Nothing here is ticked by hand: a step is done when
 * its fact is true, so editing a field anywhere (another device, the gig
 * sign-up copying skills) moves the list.
 *
 * Only skills (3+) is ENFORCED by the /get-ready gate. Alerts, location,
 * "Visit My Work" and "say hi" are non-enforced badge steps — they count
 * toward the "Ready to Work" badge but never block the user.
 */
import { ROUTES } from "@/lib/constants";

export type StepId = "alerts" | "myWork" | "skills" | "languages" | "location" | "chat";
export type StepStatus = "done" | "todo" | "locked";

export interface ChecklistFacts {
  hasPhoneChannel: boolean;
  hasEmailChannel: boolean;
  emailOnlyAlerts: boolean;
  visitedMyWork: boolean;
  skillCount: number;
  hasLanguage: boolean;
  hasCity: boolean;
  isWorkspaceMember: boolean;
  hasSentMessage: boolean;
}

export interface ChecklistStep {
  id: StepId;
  status: StepStatus;
  enforced: boolean;
  href: string;
  viaEmail?: boolean;
}

export interface Checklist {
  steps: ChecklistStep[];
  done: number;
  total: number;
  complete: boolean;
}

export const MIN_SKILLS = 3;
export const ENFORCED_STEPS: readonly StepId[] = ["skills"];
// Phone providers that reach the member outside weXare.
export const PHONE_PROVIDERS: ReadonlySet<string> = new Set([
  "telegram",
  "whatsapp_meta",
  "whatsapp_openwa",
  "sms",
]);

const done = (yes: boolean): StepStatus => (yes ? "done" : "todo");

export function buildChecklist(f: ChecklistFacts): Checklist {
  const steps: ChecklistStep[] = [
    {
      id: "alerts",
      status: done(f.hasPhoneChannel || f.hasEmailChannel),
      enforced: false,
      href: `${ROUTES.PROFILE}?tab=settings&setup=channels`,
    },
    { id: "myWork", status: done(f.visitedMyWork), enforced: false, href: ROUTES.WORK },
    {
      id: "skills",
      status: done(f.skillCount >= MIN_SKILLS),
      enforced: true,
      href: `${ROUTES.PROFILE}?setup=skills`,
    },
    {
      id: "languages",
      status: done(f.hasLanguage),
      enforced: false,
      href: `${ROUTES.PROFILE}?setup=languages`,
    },
    {
      id: "location",
      status: done(f.hasCity),
      enforced: false,
      href: `${ROUTES.PROFILE}?setup=location`,
    },
    {
      id: "chat",
      status: f.isWorkspaceMember ? done(f.hasSentMessage) : "locked",
      enforced: false,
      href: ROUTES.CHATS,
    },
  ];
  const counted = steps.filter((s) => s.status !== "locked");
  const doneCount = counted.filter((s) => s.status === "done").length;
  return {
    steps,
    done: doneCount,
    total: counted.length,
    complete: doneCount === counted.length,
  };
}

export function missingEnforced(f: ChecklistFacts): StepId[] {
  return buildChecklist(f)
    .steps.filter((s) => s.enforced && s.status !== "done")
    .map((s) => s.id);
}
